"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import { CoreFeaturesContent } from "@/types/data";
import { gsap } from "@/lib/gsap";
import FeaturesCards from "./FeaturesCards";

interface CoreFeaturesClientProps {
  content: CoreFeaturesContent;
}

export default function CoreFeaturesClient({
  content,
}: CoreFeaturesClientProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          end: "top 45%",
          toggleActions: "play none none reverse",
        },
      });

      timeline
        .fromTo(
          headerRef.current,
          { opacity: 0, x: 0, scale: 0.95, filter: "blur(8px)" },
          {
            opacity: 1,
            x: 0,
            scale: 1,
            duration: 0.8,
            ease: "power2.out",
            filter: "blur(0px)",
          }
        )
        .fromTo(
          headingRef.current,
          { opacity: 0, y: 40, filter: "blur(8px)" },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          },
          "-=0.5"
        )
        .fromTo(
          descriptionRef.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.6"
        );

      
      gsap.fromTo(
        imageRef.current,
        { opacity: 0, y: 60, scale: 0.92, filter: "blur(8px)" },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: "blur(0px)",
          duration: 0.8,
          ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          scrollTrigger: {
            trigger: imageRef.current,
            start: "top 80%",
            end: "top 40%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const { tagline, title, description, features, image } = content;

  return (
    <section
      ref={sectionRef}
      className="section-padding bg-theme-secondary text-theme-secondary-lightest "
    >
      <div className="container">
        <div className="text-center md:text-left">
          <p
            ref={headerRef}
            className="scroll-into-view text-sm font-semibold text-accent tracking-wider mb-4 flex items-center opacity-0"
          >
            <span className="w-5 h-0.5 bg-accent mr-3"></span>
            <span className="text-xs font-semibold text-accent tracking-wider uppercase">
              {tagline}
            </span>
          </p>

          <h2
            ref={headingRef}
            className="scroll-into-view mt-7 text-4xl md:text-5xl font-bold leading-tight delay-100 text-start opacity-0"
          >
            {title}
          </h2>
          <p
            ref={descriptionRef}
            className="scroll-into-view mt-6 text-lg max-w-2xl text-white delay-200 text-start opacity-0"
          >
            {description}
          </p>
        </div>

        <FeaturesCards features={features} />

        <div ref={imageRef} className="scroll-into-view mt-16 delay-[700ms] opacity-0">
          {image.src && (
            <Image
              src={image.src}
              alt={image.alt}
              width={1200}
              height={600}
              quality={85}
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 80vw, 1200px"
              className="w-full h-auto rounded-2xl object-cover shadow-2xl"
              loading="lazy"
            />
          )}
        </div>
      </div>
    </section>
  );
}
